import { useState, useEffect } from "react";
import Modal from "react-modal";
import { useNavContext } from "../context/NavContext";
import { useAuth } from "../context/AuthContext";
import Nav from "./Nav";
import Spinner from "./Spinner";
import Paginador from "./Paginador";
import CalculadorForm from "./CalculadorForm";
import { doc, deleteDoc, onSnapshot } from "firebase/firestore";
import Swal from "sweetalert2";
import { getSubCollection } from "../utils/getSubCollection";
import { useFunctionsPrevia } from "../hooks/useFunctionsPrevia";
import Breadcrumb from "./Breadcrumb";

const styleForm = {
  content: {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    padding: 0,
    fontFamily: "Inter, sans-serif",
    "::WebkitScrollbar": { width: "0px", background: "transparent" },
    msOverflowStyle: "none",
    scrollbarWidth: "none",
  },
  overlay: {
    zIndex: 101,
    backgroundColor: "rgba(0, 0, 0, 0.4)",
  },
};

const Calculador = () => {
  const { user, teamOwnerId } = useAuth();
  const ownerUid = teamOwnerId || user.uid;
  const { formatDate } = useFunctionsPrevia();
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [gastos, setGastos] = useState([]);
  const [loadingGastos, setLoadingGastos] = useState(true);
  const [selectedGastos, setSelectedGastos] = useState([]);
  const [selectedGasto, setSelectedGasto] = useState(null);
  const [config, setConfig] = useState(null);
  const [formKey, setFormKey] = useState(0);
  const {
    setSearchTerm,
    proximasAVencer,
    proximosEventos,
    searchTerm,
    itemsPerPage = 25,
    currentPage = 1,
    handlePageChange,
    handleItemsPerPageChange,
  } = useNavContext();

  useEffect(() => {
    setLoadingGastos(true);
    const gastosCol = getSubCollection(ownerUid, "gastos");
    const unsub = onSnapshot(
      gastosCol,
      (snap) => {
        setGastos(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingGastos(false);
      },
      (err) => {
        console.error("Error real-time gastos:", err);
        setLoadingGastos(false);
      }
    );
    return () => unsub();
  }, [ownerUid]);

  // Helpers
  const handleRowClick = (gasto) => {
    setSelectedGasto(gasto);
    setFormKey((k) => k + 1);
    setMostrarFormulario(true);
  };

  const handleSelectGasto = (id) =>
    setSelectedGastos((sel) =>
      sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]
    );

  const handleSelectAllGastos = (e) =>
    setSelectedGastos(e.target.checked ? gastos.map((g) => g.id) : []);

  const openModal = () => {
    setSelectedGasto(null);
    setFormKey((k) => k + 1);
    setMostrarFormulario(true);
  };

  const closeModal = () => setMostrarFormulario(false);

  const handleDeleteSelected = async () => {
    if (!selectedGastos.length) return;
    const { isConfirmed } = await Swal.fire({
      title: "¿Seguro que deseas eliminar?",
      text: "No podrás revertir esta acción",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085b6",
      confirmButtonText: "Sí, eliminar",
    });
    if (!isConfirmed) return;

    try {
      const gastosCol = getSubCollection(ownerUid, "gastos");
      await Promise.all(
        selectedGastos.map((id) => deleteDoc(doc(gastosCol, id)))
      );
      setSelectedGastos([]);
      Swal.fire("Eliminados", "Gastos eliminados correctamente", "success");
      // El listener real-time ya actualizará `gastos`
    } catch (err) {
      console.error("Error eliminando gastos:", err);
      Swal.fire("Error", "No se pudo eliminar", "error");
    }
  };

  // 📑 Filtrar
  const filteredGastos = gastos.filter((g) => {
    const t = searchTerm.trim().toLowerCase();
    if (!t) return true;
    return [g.nombre, g.cliente, String(g.numero)].some((f) =>
      f?.toLowerCase().includes(t)
    );
  });

  return (
    <>
      <div className="no-sticky">
        <Nav
          handleSearch={setSearchTerm}
          proximasAVencer={proximasAVencer}
          proximosEventos={proximosEventos}
        />

        {/* FILTROS / BOTONES */}
        <div className="task-filters" style={{ flexWrap: "wrap" }}>
          <div
            style={{
              flexBasis: "100%", // ocupa toda la fila
              padding: "0 4px", // mismo gutter que usas
              margin: "0 0 5px", // respirito arriba/abajo
            }}
          >
            <Breadcrumb />
          </div>
          <div className="header-actions-proyectos">
            <button className="action-button-proyectos">
              <i className="bi bi-funnel-fill" /> Todos
              <i className="bi bi-chevron-down" />
            </button>
            <div className="buscador-pedidos">
              <i className="bi bi-search" />
              <input
                type="text"
                className="buscador-input"
                placeholder="Buscar gastos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div
            className="task-filters-right"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <button
              className="delete-button"
              style={{
                visibility: selectedGastos.length ? "visible" : "hidden",
              }}
              onClick={handleDeleteSelected}
            >
              Eliminar
            </button>
            <button className="new-task-button" onClick={openModal}>
              + Nuevo
            </button>
          </div>
        </div>

        {/* TABLA */}
        <div className="cotizaciones-table">
          <div className="tabla-cotizaciones-wrapper">
            <div className="tabla-cotizaciones-content">
              {loadingGastos ? (
                <Spinner />
              ) : gastos.length === 0 ? (
                // 1️⃣ Aún no hay ningún gasto
                <div className="no-resultados">
                  <div
                    className="mensaje-sin-resultados"
                    style={{ marginTop: "30px" }}
                  >
                    <p style={{ fontSize: "14px" }}>Aún no hay ningún gasto.</p>
                  </div>
                  <div className="imagen-sin-resultados">
                    <img
                      src="/img/no-data.svg"
                      alt="Sin gastos"
                      width={350}
                      height={350}
                    />
                  </div>
                </div>
              ) : filteredGastos.length > 0 ? (
                // 2️⃣ Hay gastos y alguno coincide con la búsqueda
                <table>
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          onChange={handleSelectAllGastos}
                          checked={
                            selectedGastos.length === filteredGastos.length
                          }
                          className="checkbox-select-all check"
                        />
                      </th>
                      <th>Nombre</th>
                      <th>No.</th>
                      <th>Cliente</th>
                      <th>Fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredGastos.map((g) => (
                      <tr key={g.id} onClick={() => handleRowClick(g)}>
                        <td
                          className="check"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={selectedGastos.includes(g.id)}
                            onChange={() => handleSelectGasto(g.id)}
                            className="checkbox"
                          />
                        </td>
                        <td>{g.nombre}</td>
                        <td>{g.numero}</td>
                        <td>{g.cliente}</td>
                        <td>
                          <div className="estilos-fechas">
                            {formatDate(g.fechaVencimiento)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                // 3️⃣ Hay gastos, pero ninguno coincide con el término de búsqueda
                <div className="no-resultados">
                  <div
                    className="mensaje-sin-resultados"
                    style={{ marginTop: "30px" }}
                  >
                    <p style={{ fontSize: "14px" }}>
                      No hay gastos que coincidan con tu búsqueda.
                    </p>
                  </div>
                  <div className="imagen-sin-resultados">
                    <img
                      src="/img/noSearch.svg"
                      alt="Sin resultados"
                      className="sin-resultados-imagen"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="tabla-cotizaciones-footer">
              <div className="paginador-container">
                <Paginador
                  totalItems={filteredGastos.length}
                  itemsPerPage={itemsPerPage}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                />
              </div>
            </div>
          </div>
        </div>

        {/* MODAL FORM */}
        <Modal
          isOpen={mostrarFormulario}
          onRequestClose={closeModal}
          contentLabel="Formulario de Gasto"
          style={styleForm}
        >
          <button className="cerrar-button" onClick={closeModal}>
            <i className="bi bi-x-lg" />
          </button>
          <Nav
            handleSearch={setSearchTerm}
            proximasAVencer={proximasAVencer}
            proximosEventos={proximosEventos}
          />
          <CalculadorForm
            key={formKey}
            closeModal={() => setMostrarFormulario(false)}
            initialData={selectedGasto}
            config={config}
            setConfig={setConfig}
          />
        </Modal>
      </div>
    </>
  );
};

export default Calculador;
